import { Download, FileText, Image, Video, Music, File, FileSpreadsheet, Presentation } from 'lucide-react';
import { WorkerDocument } from '../../../types/worker.types';
import { workerService } from '../../../services/worker.service';
import { Card, CardContent, Button } from '../../ui';

interface DocumentCardProps {
  document: WorkerDocument;
  onDownload?: (document: WorkerDocument) => void;
}

export function DocumentCard({ document, onDownload }: DocumentCardProps) {
  const handleDownload = async () => {
    try {
      if (onDownload) {
        onDownload(document);
      } else {
        await workerService.downloadDocument(document.file_url, document.name);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const getFileIcon = (fileType: string) => {
    const type = fileType.toLowerCase();
    if (type.includes('pdf') || type.includes('text')) return FileText;
    if (type.includes('image')) return Image;
    if (type.includes('video')) return Video;
    if (type.includes('audio')) return Music;
    if (type.includes('excel') || type.includes('spreadsheet')) return FileSpreadsheet;
    if (type.includes('powerpoint') || type.includes('presentation')) return Presentation;
    return File;
  };

  const getFileTypeColor = (fileType: string) => {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return 'text-red-600 bg-red-50';
    if (type.includes('image')) return 'text-green-600 bg-green-50';
    if (type.includes('video')) return 'text-purple-600 bg-purple-50';
    if (type.includes('audio')) return 'text-orange-600 bg-orange-50';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'text-emerald-600 bg-emerald-50';
    if (type.includes('powerpoint') || type.includes('presentation')) return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-50';
  };

  const FileIcon = getFileIcon(document.file_type);
  const colorClass = getFileTypeColor(document.file_type);

  return (
    <Card hover className="group">
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colorClass}`}>
              <FileIcon className="h-5 w-5" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">
                {document.name}
              </h3>
              <p className="text-sm text-gray-500">
                {workerService.formatFileSize(document.file_size)}
              </p>
            </div>
          </div>

          <Button
            onClick={handleDownload}
            variant="outline"
            size="sm"
            icon={Download}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>

        {document.category && (
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {document.category}
            </span>
          </div>
        )}

        {document.tags && document.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {document.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
              >
                {tag}
              </span>
            ))}
            {document.tags.length > 3 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                +{document.tags.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {new Date(document.created_at).toLocaleDateString('fr-FR')}
          </span>
          <Button
            onClick={handleDownload}
            variant="primary"
            size="sm"
            icon={Download}
          >
            Télécharger
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}